import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Shield, Clock, Star } from "lucide-react";
import heroHouse from "@/assets/chantier-facade.jpg?w=1600&format=webp";
import { submitFormAndRedirectToWhatsApp } from "@/lib/submitForm";

const RotatingBadge = ({ text, icon: Icon }: { text: string; icon: React.ElementType }) => (
  <div className="relative w-24 h-24 md:w-28 md:h-28">
    <motion.div
      className="absolute inset-0 rounded-full border-2 border-dashed border-primary/50"
      animate={{ rotate: 360 }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
    />
    <div className="absolute inset-2 rounded-full bg-secondary/80 backdrop-blur-sm flex flex-col items-center justify-center text-center">
      <Icon className="text-primary mb-1" size={20} />
      <span className="text-[10px] md:text-xs font-heading font-bold text-secondary-foreground leading-tight px-1">{text}</span>
    </div>
  </div>
);

const HeroSection = () => {
  const [formData, setFormData] = useState({ nom: "", telephone: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitFormAndRedirectToWhatsApp({
      nom: formData.nom,
      telephone: formData.telephone,
      message: formData.message,
      source: "hero-accueil",
    });
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ nom: "", telephone: "", message: "" });
  };

  return (
    <section
      id="accueil"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img src={heroHouse} alt="Maison rénovée par Habitat Prestige 89 à Sens – peinture façade et rénovation habitat" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, hsla(0,0%,0%,0.85) 0%, hsla(0,0%,0%,0.6) 100%)" }} />
      </div>

      <div className="container-custom w-full section-padding relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            <div className="flex gap-4 mb-8">
              <RotatingBadge text="Devis Gratuit" icon={Star} />
              <RotatingBadge text="Garantie Qualité" icon={Shield} />
              <RotatingBadge text="Sous 48h" icon={Clock} />
            </div>

            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
              <span className="text-secondary-foreground">Artisan Peinture &</span>
              <br />
              <span className="text-gradient-primary">Rénovation Habitat</span>
              <br />
              <span className="text-secondary-foreground">à Sens (89)</span>
            </h1>

            <p className="text-lg md:text-xl text-secondary-foreground/70 mb-8 max-w-lg font-body">
              Peinture intérieure &amp; extérieure, démoussage toiture, hydrofuge façade et petite maçonnerie dans l'Yonne.<br />
              <strong className="text-primary">Devis gratuit sous 48h – Artisan local.</strong>
            </p>

            <div className="flex flex-wrap gap-6 items-center">
              <a
                href="tel:+33632864661"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-xl font-heading font-bold text-lg hover:brightness-110 transition-all animate-pulse-glow"
              >
                Appeler maintenant
              </a>
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map(i => (
                  <Star key={i} size={20} className="text-primary fill-primary" />
                ))}
                <span className="text-secondary-foreground/60 text-sm ml-2">5/5 Google</span>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
          >
            <div className="bg-background/95 backdrop-blur-lg rounded-2xl p-6 md:p-8" style={{ boxShadow: "var(--shadow-card)" }}>
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-2">
                Devis Express <span className="text-primary">Gratuit</span>
              </h2>
              <p className="text-muted-foreground text-sm mb-6">Réponse sous 48h garantie</p>

              {submitted ? (
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="text-center py-12"
                >
                  <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Shield className="text-primary" size={32} />
                  </div>
                  <p className="font-heading font-bold text-lg text-foreground">Demande envoyée !</p>
                  <p className="text-muted-foreground text-sm mt-1">Nous vous recontactons sous 48h.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <input
                    type="text"
                    placeholder="Votre nom"
                    required
                    aria-label="Votre nom"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  />
                  <input
                    type="tel"
                    placeholder="Votre téléphone"
                    required
                    aria-label="Votre téléphone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                  />
                  <textarea
                    placeholder="Décrivez votre projet..."
                    rows={3}
                    aria-label="Description du projet"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all resize-none"
                  />
                  <button
                    type="submit"
                    className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground px-6 py-4 rounded-xl font-heading font-bold text-lg hover:brightness-110 transition-all"
                    style={{ boxShadow: "var(--shadow-cta)" }}
                  >
                    <Send size={20} />
                    Demander mon devis gratuit
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
